/* eslint-disable i18next/no-literal-string */

import { Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useQuery } from 'api';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box } from 'ui';

import { AutocompleteField, type AutocompleteFieldProps } from '../AutocompleteField/AutocompleteField';
import { parseAddressComponents } from './parseAddressComponents';
import type { AddressDetails } from './types';

type AutocompletePrediction = google.maps.places.AutocompletePrediction;
type PlaceDetailsRequest = google.maps.places.PlaceDetailsRequest;
type PlaceResult = google.maps.places.PlaceResult;

const PLACE_DETAILS_COMPONENTS = ['address_components', 'geometry', 'formatted_address'];

export type AddressAutocompleteProps = Omit<AutocompleteFieldProps<AutocompletePrediction>, 'options'> & {
  required?: boolean;
  onAddressSelected?: (address: AddressDetails | null) => void;
};

export interface GetListResponse<TData> {
  data: TData[];
  total: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export const AddressAutocomplete = ({
  name,
  label = 'Enter address',
  required = false,
  onAddressSelected,
  ...rest
}: AddressAutocompleteProps) => {
  const { setValue } = useFormContext();
  const [autocompleteValue, setAutoCompleteValue] = useState<AutocompletePrediction | null>(null);
  const [query, setQuery] = useState('');
  const map = useMap();
  const places = useMapsLibrary('places');

  const autocompleteService = useMemo(() => {
    if (!places) return null;
    return new places.AutocompleteService();
  }, [places]);

  const placesService = useMemo(() => {
    if (!places || !map) return null;
    return new places.PlacesService(map);
  }, [places, map]);

  const fetchPredictions = async (inputValue: string): Promise<GetListResponse<AutocompletePrediction>> => {
    if (!autocompleteService) {
      return { data: [], total: 0 };
    }

    return await new Promise<GetListResponse<AutocompletePrediction>>((resolve) => {
      autocompleteService.getPlacePredictions({ input: inputValue }, (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          resolve({ data: predictions, total: predictions.length });
        } else {
          resolve({ data: [], total: 0 });
        }
      });
    });
  };

  const { data, isLoading } = useQuery({
    enabled: Boolean(query && autocompleteService),
    queryFn: async () => {
      if (!query || !autocompleteService) {
        return { data: [], total: 0 };
      }
      return await fetchPredictions(query);
    },
    queryKey: ['address-autocomplete', query],
  });

  const fetchPlaceDetails = async (placeId: string): Promise<PlaceResult | null> => {
    if (!placesService) {
      return null;
    }

    const request: PlaceDetailsRequest = {
      fields: PLACE_DETAILS_COMPONENTS,
      placeId: placeId,
    };

    try {
      return await new Promise<PlaceResult>((resolve, reject) => {
        placesService.getDetails(request, (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            resolve(place);
          } else {
            reject(status);
          }
        });
      });
    } catch (_error) {
      return null;
    }
  };

  const handleOptionSelected = async (
    _event: React.SyntheticEvent<Element, Event>,
    newValue: AutocompletePrediction | null
  ) => {
    setAutoCompleteValue(newValue);
    let addressDetails;
    if (!newValue) {
      addressDetails = null;
    }
    if (newValue && newValue.place_id) {
      const placeDetails = await fetchPlaceDetails(newValue.place_id);
      if (!placeDetails) {
        addressDetails = null;
      } else {
        addressDetails = parseAddressComponents(placeDetails);
      }
    }
    setValue(name, addressDetails);
    onAddressSelected?.(addressDetails || null);
  };

  return (
    <>
      <AutocompleteField<AutocompletePrediction>
        options={data?.data || []}
        loading={isLoading}
        value={autocompleteValue}
        onChange={handleOptionSelected}
        name={name}
        label={label}
        required={required}
        getOptionLabel={(option) => option.description || ''}
        isOptionEqualToValue={(option, value) => option.place_id === value?.place_id}
        valueField="place_id"
        filterOptions={(options, state) => {
          setQuery(state.inputValue);
          return options;
        }}
        {...rest}
      />
      <Box sx={{ height: 0, visibility: 'hidden' }}>
        <Map
          defaultZoom={3}
          defaultCenter={{ lat: 51.5072, lng: 0.1276 }}
          gestureHandling={'greedy'}
          disableDefaultUI={true}
        />
      </Box>
    </>
  );
};
